import arrayMutators from "final-form-arrays";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import AppBar from "src/components/AppBar/AppBar";
import FormModal from "src/components/FormModalV2/FormModal";
import { IconName } from "src/components/Icon/Icon";
import Toast from "src/components/Toast/Toast";
import { IRootState } from "src/store/reducers";
import { fetchMemoDetails, fetchMemoOptions, saveMemo } from "src/store/reducers/applicationMemos";
import { fetchApplication } from "src/store/reducers/applications";
import * as S from "../../../../constants/StringConstants";
import css from "./mailCSS";
import MemoForm from "./MemoForm";

const clear = ([name], state, { changeValue }) => {
  changeValue(state, name, () => null);
};

enum SubmitStatus {
  none,
  ok,
  error,
  dismissed,
}

const MemoDetail = () => {
  const { appID, memoID } = useParams<{ appID: string; memoID: string }>();
  const [memoTypeID, setMemoTypeID] = useState<string | null>();
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>(SubmitStatus.none);
  const dispatch = useDispatch();
  const history = useHistory();
  const isNew = memoID === "new";

  const { memoDetails, isFetching, isSubmitting } = useSelector((state: IRootState) => ({
    isFetching: state.applicationMemos.loading,
    isSubmitting: state.applicationMemos.submitting,
    memoDetails: {
      ...state.applicationMemos.memoDetails,
      memoTypeID,
      description: state.applicationMemos.memoDetails?.selectMemoTypeList?.find(m => m.value === memoTypeID)?.key || "",
      toEmailAddresses: state.applicationMemos.memoDetails?.toEmailAddresses || [],
      ccEmailAddresses: state.applicationMemos.memoDetails?.ccEmailAddresses || [],
      attachments: [],
    },
  }));

  useEffect(() => {
    dispatch(fetchApplication(appID));
    if (isNew) {
      dispatch(fetchMemoOptions(appID));
    } else {
      dispatch(fetchMemoDetails(appID, memoID));
    }
  }, [appID, memoID, isNew]);

  useEffect(() => {
    if (isNew) {
      dispatch(fetchMemoOptions(appID, memoTypeID));
    }
  }, [appID, memoTypeID]);

  useEffect(() => {
    if (submitStatus === SubmitStatus.dismissed) {
      history.goBack();
    }
  }, [submitStatus]);

  const handleSubmit = async (values: any) => {
    // inject CSS if needed - HACK!
    let body = values.memoBody;
    if (body.indexOf("<style>") === -1) {
      body = css + body;
    }

    const payload = {
      Subject: values.subject,
      Body: body,
      TOAddresses: values.toEmailAddresses.join(","),
      CCAddresses: values.ccEmailAddresses.join(","),
      MemoTemplateID: values.memoTypeID,
      Description: values.description,
    };
    try {
      await dispatch(saveMemo(appID, payload, values.attachments));
      setSubmitStatus(SubmitStatus.ok);
    } catch (error) {
      setSubmitStatus(SubmitStatus.error);
    }
  };

  const handleValidate = values => {
    // handle this first - https://github.com/final-form/final-form/issues/35
    if (values.toEmailAddresses.length === 0) {
      return "to required";
    }
    const errors: any = {};
    if (!values.memoTypeID) {
      errors.memoTypeID = "required";
    }
    if (!values.subject) {
      errors.subject = "required";
    }
    if (!values.memoBody) {
      errors.memoBody = "required";
    }
    return errors;
  };

  return (
    <div className="comments-detail">
      <AppBar icon={IconName.Applications} title={S.APP_MEMO_TITLE} loading={isFetching} />

      <Toast message={S.SUBMITTING} open={isSubmitting} onClose={() => {}} />

      <FormModal
        open={submitStatus != SubmitStatus.none}
        saveLabel={null}
        onCancel={() => setSubmitStatus(SubmitStatus.dismissed)}
        onSubmit={() => {}}
        cancelLabel={S.OK}>
        <h3>
          {submitStatus === SubmitStatus.ok
            ? S.APP_MEMO_SENT
            : submitStatus === SubmitStatus.error
              ? S.APP_MEMO_ERROR
              : null}
        </h3>
      </FormModal>

      <div className="comments-detail__inner-container">
        <Form
          mutators={{ ...arrayMutators, clear }}
          initialValues={memoDetails}
          onSubmit={handleSubmit}
          validate={handleValidate}
          render={({ handleSubmit, valid, submitting }) => {
            return (
              <form onSubmit={handleSubmit}>
                <MemoForm
                  memoTypes={memoDetails?.selectMemoTypeList || []}
                  deliveryTypes={memoDetails?.selectDeliveryTypeList || []}
                  states={memoDetails?.selectStateList || []}
                  onTypeChange={(value: string) => setMemoTypeID(value)}
                />
                <div className="app-builder__action_buttons">
                  <button type="button" className="button-v2 button-v2__secondary" onClick={() => history.goBack()}>
                    {S.APP_MEMO_DETAIL_CANCEL}
                  </button>
                  <button type="submit" className="button-v2 button-v2__primary" disabled={!valid || submitting}>
                    {S.APP_MEMO_DETAIL_SEND}
                  </button>
                </div>
              </form>
            );
          }}
        />
      </div>
    </div>
  );
};

export default MemoDetail;
